import { Injectable } from '@angular/core';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import { MyOrganizationFacade, getRolesAndPermission } from '@piqet-angular/data';
import {
  dblclickTooltipDef,
  defaultColumn,
  DELETE_RECORD_LABEL,
  grid_widths_map,
  mainModuleName,
  rolesModuleName,
} from '@piqet-angular/shared';
import { right } from '@popperjs/core';
import { ColDef } from 'ag-grid-community';
import { Subject, take } from 'rxjs';
import { cloneDeep } from 'lodash';
import { NumericCellEditor } from 'libs/ui/src/lib/numeri-cell-editor/numeric-cell-editor.component';

@Injectable({
  providedIn: 'root',
})
export class UploadSKUDataGridService {
  deleteSKU = new Subject<[any]>();
  deleteSKUData$ = this.deleteSKU.asObservable();

  editSKU = new Subject<[any, any]>();
  editSKUData$ = this.editSKU.asObservable();

  constructor(
    private swalService: SwalService,
    private myOrganizationFacade: MyOrganizationFacade
  ) {}

  getSKUListGrid(): ColDef[] {
    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'projectName',
      headerName: 'Project name',
      field: 'projectName',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockVisible: true,
      wrapHeaderText: true,
      cellStyle: { 'text-decoration': 'underline' },
      cellRenderer: (params: any) => {
        const cellValue = params.value;
        const cellElement = document.createElement('span');
        cellElement.style.cursor = 'pointer';
        cellElement.innerHTML = `${cellValue}`;
        cellElement.title = dblclickTooltipDef;
        cellElement.addEventListener('dblclick', () => {
          this.editSKU.next(params.data);
        });
        return cellElement;
      },
    });
    colDef.push({
      ...defaultColumn,
      colId: 'projectCode',
      headerName: 'Project code',
      field: 'projectCode',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockVisible: true,
      wrapHeaderText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'ScenarioName',
      headerName: 'Scenario name',
      field: 'scenarioName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockVisible: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'ScenarioCode',
      headerName: 'Scenario code',
      field: 'scenarioCode',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockVisible: true,
      wrapHeaderText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Uuid',
      headerName: 'UUID',
      field: 'uuid',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockVisible: true,
      wrapHeaderText: true,
      cellRenderer: 'inlineTooltipComponent',
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Department',
      headerName: 'Department',
      field: 'department',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockVisible: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Category',
      headerName: 'Category',
      field: 'category',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockVisible: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'SubCategory',
      headerName: 'Sub-category',
      field: 'subCategory',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockVisible: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      minWidth: grid_widths_map.sm_column_xxm,
      maxWidth: grid_widths_map.sm_column_xs,
      cellRendererParams: {
        showEdit: true,
        showDelete: true,
        editData: (data: any) => {
          this.editSKU.next(data);
        },
        deleteData: (data: any) => {
          this.deleteConfirmation(data);
        },
      },
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'actionRenderer',
      lockVisible: true,
      lockPosition: right,
      resizable: false,
    });

    return colDef;
  }

  addSKUYearGrid(): ColDef[] {
    let enableEditing: any;
    let resetData: any;
    let newValue: any;
    let newValueProductGroup: any;
    let yearProcessId: any;
    let productGroupId: any;
    let isCheckProductGroup = false;
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'NumberOfUnitsProduced',
      headerName: 'Number of units produced',
      field: 'numberOfUnitsProduced',
      cellClass: 'mainGridEdit',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockVisible: true,
      editable: true,
      cellEditor: NumericCellEditor,
      valueSetter: (params: any) => {
        const newValInt = params.newValue;
        const valueChanged = !resetData ? params.data.numberOfUnitsProduced !== newValInt : false;
        params = cloneDeep(params);
        if (valueChanged) {
          params.data.numberOfUnitsProduced =
            typeof params.oldValue == 'number' ? parseFloat(newValInt) : newValInt;
        }
        params.data['enableEditing'] = enableEditing;
        const colId = params.colDef.field;
        const nodeId = params.data.id;
        this.myOrganizationFacade.checkUpdateActionSKU$.pipe(take(1)).subscribe((flag: boolean) => {
          flag &&
            this.myOrganizationFacade.updateSKUProcessActions(
              params.data,
              'edit',
              null,
              nodeId,
              colId
            );
        });
        return valueChanged;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Year',
      headerName: 'Year',
      field: 'year',
      cellClass: 'mainGridEdit',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      editable: true,
      lockVisible: true,
      cellEditor: 'GridCellRendererComponent',
      cellEditorParams: {
        from: 'addSKU',
        editData: (data: any) => {
          newValue = data.year;
          yearProcessId = data.yearId;
        },
      },
      cellRenderer: (params: any) => {
        return `<div class="truncated-text" title="${params.value}">${
          params.value ? params.value : ''
        }</div>`;
      },
      valueSetter: (params: any) => {
        const newVal = !!newValue ? newValue : 'Select process';
        const valueChanged =
          !!newValue && !resetData ? params.data[params.colDef.field] !== newVal : false;
        params.data = cloneDeep(params.data);
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['enableEditing'] = enableEditing;
          const colId = params.colDef.field;
          const nodeId = params.data.id;
          this.myOrganizationFacade.updateSKUProcessActions(
            params.data,
            'edit',
            null,
            nodeId,
            colId
          );
          newValue = '';
          yearProcessId = '';
        }
        return valueChanged;
      },
      valueGetter: params => {
        return params.data.year;
      },
    });
    // colDef.push({
    //   ...defaultColumn,
    //   colId: 'ProductGroup',
    //   headerName: 'Product group',
    //   field: 'productGroupName',
    //   cellClass: 'mainGridEdit',
    //   wrapText: true,
    //   autoHeight: true,
    //   resizable: false,
    //   editable: true,
    //   lockVisible: true,
    //   cellEditor: 'GridCellRendererComponent',
    //   cellEditorParams: {
    //     from: 'addSKUProductGroup',
    //     editData: (data: any) => {
    //       newValueProductGroup = data.productGroupName;
    //       productGroupId = data.productGroupId;
    //     }
    //   },
    //   cellRenderer: (params: any) => {
    //     return `<div class="truncated-text" title="${params.value}">${params.value ? params.value : 'Select a product group'}</div>`;
    //   },
    //   valueSetter: (params: any) => {
    //     const newVal = !!newValueProductGroup ? newValueProductGroup : 'Select process';
    //     const valueChanged = (!!newValueProductGroup && !resetData) ? params.data[params.colDef.field] !== newVal : false;
    //     const checkUpdateClear = isCheckProductGroup && newValueProductGroup == 'Select a product group';
    //     params.data = cloneDeep(params.data);
    //     if (valueChanged && !checkUpdateClear) {
    //       params.data[params.colDef.field] = typeof (params.oldValue) == 'number' ? parseFloat(newVal) : newVal;
    //       params.data['enableEditing'] = enableEditing;
    //       params.data['productGroupId'] = productGroupId;
    //       const colId = params.colDef.field;
    //       const nodeId = params.data.id;
    //       this.myOrganizationFacade.updateSKUProcessActions(params.data, 'edit', null, nodeId, colId);
    //       newValueProductGroup = '';
    //       productGroupId = '';
    //     }
    //     return valueChanged;
    //   },
    //   valueGetter: (params) => {
    //     return params.data.productGroupName;
    //   },
    // });

    colDef.push({
      ...defaultColumn,
      colId: 'Actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      minWidth: grid_widths_map.sm_column_xxm,
      maxWidth: grid_widths_map.sm_column_xs,
      cellRenderer: 'inlineActionButtonComponent',
      cellRendererParams: {
        showInlineEdit: true,
        showInlineDelete: true,
        copyData: (data: any) => {
          return false;
        },
        inlineEditData: (data: any) => {
          enableEditing = data.data.enableEditing;
          resetData = data.data.resetData;
          isCheckProductGroup = data.data.isCheckProductGroup;
        },
        deleteInlineGrid: (data: any) => {
          this.alertConfirmation(data);
        },
      },
      cellClass: 'mainGridEdit',
      lockVisible: true,
      lockPosition: right,
      resizable: false,
      pinned: right,
    });

    return colDef;
  }

  deleteConfirmation(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.deleteSKU.next([data]);
      }
    });
  }

  alertConfirmation(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.myOrganizationFacade.deleteSKUProcessActions(data, 'delete');
      }
    });
  }

  triggerSKUPopup() {
    const data: any = { id: 0 };
    this.editSKU.next(data);
  }
}
